import type { CatalogUnit, Product } from "@/lib/types";
import { basePricePerKg, effectiveUnitPrice, round2 } from "@/lib/weightPricing";

export interface ProductPriceQuote {
  /** Preço da combinação: match exato (final, sem 10%) ou derivado do KG do mesmo corte (+10% se < 1kg). */
  pricePerUnit?: number;
  /** Id da unidade a que o `pricePerUnit` se refere (a selecionada). */
  unitId?: string;
  /** A combinação existe comercialmente (match exato OU conversão legítima a partir do KG do corte)? */
  available: boolean;
  /** Se o acréscimo de venda fracionada (10%) incide (só no caminho de conversão < 1kg). */
  surchargeApplied?: boolean;
}

/** Chave de corte em `priceByCutAndUnit`: `""` = preço base (sem corte). */
function cutKey(cut?: string): string {
  return cut == null || cut.trim() === "" ? "" : cut.trim();
}

/**
 * INTERIM: identifica o quilograma (peso base) pelo nome canônico "Kg". Encapsulado para, no futuro,
 * trocar por uma propriedade específica da unidade sem refazer a lógica. Espelha
 * `CommercialPricingService.isKilogramReference` no backend — NÃO usar `group` (é só apresentação).
 */
export function isKilogramReference(unit: CatalogUnit | undefined): boolean {
  return !!unit && !!unit.name && unit.name.trim().toLowerCase() === "kg";
}

/**
 * Espelho EXATO do backend (`CommercialPricingService.resolveFromTable` + `WeightPricingService`):
 *   1. MATCH EXATO — `priceByCutAndUnit[corte][unidade]` cadastrado → preço final, SEM os 10%.
 *   2. CONVERSÃO LEGÍTIMA — senão, SOMENTE a partir da entrada do QUILOGRAMA do MESMO corte:
 *      `preço/kg × fator` (+10% quando a embalagem/total < 1 kg).
 *   3. Sem exato e sem KG do corte → indisponível (`available: false`, sem preço).
 * NUNCA converte de outra embalagem/unidade comercial, de outro corte, nem pelo "primeiro registro".
 */
export function resolveProductPriceQuote(
  product: Product,
  selectedUnit: string,
  catalogUnits: CatalogUnit[],
  quantity: number = 1,
  cut?: string,
): ProductPriceQuote {
  const selectedUnitObj = catalogUnits.find((u) => u.name === selectedUnit);
  if (!selectedUnitObj) {
    return { available: false };
  }
  const selectedUnitId = selectedUnitObj.id;
  const cutEntries = product.priceByCutAndUnit?.[cutKey(cut)] ?? {};

  // 1. Match exato: preço cadastrado para a unidade+corte → valor final, sem acréscimo.
  const exact = cutEntries[selectedUnitId];
  if (exact != null) {
    return { pricePerUnit: round2(exact), unitId: selectedUnitId, available: true, surchargeApplied: false };
  }

  // 2. Conversão legítima: só a partir do KG do mesmo corte (com +10% para embalagem/total < 1 kg).
  const kgUnit = catalogUnits.find((u) => isKilogramReference(u) && cutEntries[u.id] != null);
  if (kgUnit) {
    const perKg = basePricePerKg(cutEntries[kgUnit.id], kgUnit.conversionToKg);
    const eff = effectiveUnitPrice(perKg, quantity, selectedUnitObj.conversionToKg);
    return {
      pricePerUnit: eff.pricePerUnit,
      unitId: selectedUnitId,
      available: true,
      surchargeApplied: eff.surchargeApplied,
    };
  }

  // 3. Sem match exato e sem KG do corte → combinação indisponível.
  return { unitId: selectedUnitId, available: false };
}

function unitNamesOf(product: Product): string[] {
  return product.availableUnits && product.availableUnits.length > 0 ? product.availableUnits : [product.defaultUnit];
}

/** Cortes (nomeados) ofertáveis para a unidade: aqueles com match exato OU deriváveis do KG do corte. */
export function offerableCutsForUnit(product: Product, unitName: string, catalogUnits: CatalogUnit[]): string[] {
  return (product.availableCuts ?? []).filter(
    (c) => resolveProductPriceQuote(product, unitName, catalogUnits, 1, c).available,
  );
}

/** Unidades ofertáveis: as que têm ao menos uma combinação disponível (exata ou derivada do KG). */
export function offerableUnits(product: Product, catalogUnits: CatalogUnit[]): string[] {
  const cuts = product.availableCuts && product.availableCuts.length > 0 ? product.availableCuts : [undefined];
  return unitNamesOf(product).filter((u) =>
    cuts.some((c) => resolveProductPriceQuote(product, u, catalogUnits, 1, c).available),
  );
}

/** Menor preço ofertado do produto (resumo "a partir de" da vitrine) — só combinações reais/deriváveis. */
export function cheapestOfferedCombo(
  product: Product,
  catalogUnits: CatalogUnit[],
): { price: number; unitId: string } | undefined {
  const cuts = product.availableCuts && product.availableCuts.length > 0 ? product.availableCuts : [undefined];
  let best: { price: number; unitId: string } | undefined;
  for (const u of unitNamesOf(product)) {
    for (const c of cuts) {
      const q = resolveProductPriceQuote(product, u, catalogUnits, 1, c);
      if (q.available && q.pricePerUnit != null && q.unitId && (best === undefined || q.pricePerUnit < best.price)) {
        best = { price: q.pricePerUnit, unitId: q.unitId };
      }
    }
  }
  return best;
}
